import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Loading } from '@/components/OpsUI';
import { DraftCard } from './DraftCard';
import { Composer } from './Composer';
import { MessageBubble } from './MessageBubble';
import { TicketButton } from './TicketButton';

export function ConversationThread({ id, canAct, onChange }) {
  const [conv, setConv] = useState(null);
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const load = useCallback(() => api.get(`/whatsapp/conversations/${id}`).then(({ data }) => setConv(data)).catch(() => setConv(false)), [id]);
  const refresh = useCallback(() => { load(); onChange(); }, [load, onChange]);

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  const count = conv?.messages?.length;
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [count]);

  if (conv === null) return <Loading />;
  if (conv === false) return <Card className="p-8 text-sm" data-testid="whatsapp-thread-error">No se encontró la conversación.</Card>;

  const toggleAi = async (v) => {
    try { await api.patch(`/whatsapp/conversations/${id}`, { ai_enabled: v }); refresh(); } catch (e) { toast.error(errText(e)); }
  };
  const suggest = async () => {
    setBusy(true);
    try { await api.post(`/whatsapp/conversations/${id}/suggest`); refresh(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const c = conv.contact || {};
  const draft = conv.messages.find((m) => m.status === 'draft');
  const messages = conv.messages.filter((m) => m.status !== 'draft');

  return (
    <Card className="flex flex-col overflow-hidden" data-testid="whatsapp-thread">
      <div className="p-4 border-b border-[hsl(var(--border))] flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold" data-testid="whatsapp-thread-name">{c.pastor || conv.name || conv.phone}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">
            {conv.phone}{c.church ? ` · ${c.church}` : ''}{c.campaign_name ? ` · ${c.campaign_name}` : ''}
          </div>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${conv.window_open ? 'border-emerald-500/40 text-emerald-300' : 'border-zinc-500/40 text-zinc-400'}`} data-testid="whatsapp-window-status">
          {conv.window_open ? 'Ventana 24 h abierta' : 'Ventana cerrada'}
        </span>
        <TicketButton conv={conv} canAct={canAct} onDone={refresh} />
        {canAct && (
          <>
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={conv.ai_enabled !== false} onCheckedChange={toggleAi} data-testid="whatsapp-ai-toggle" />
              {conv.ai_enabled !== false ? 'Agente IA activo' : 'Control humano'}
            </label>
            <Button size="sm" variant="outline" onClick={suggest} disabled={busy || !conv.window_open}
              title={conv.window_open ? '' : 'La ventana de 24 h está cerrada: no se podría enviar la respuesta'} data-testid="whatsapp-suggest-button">
              <Sparkles className="h-4 w-4 mr-1" />{busy ? 'Pensando...' : 'Sugerir respuesta'}
            </Button>
          </>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[520px] bg-[hsl(var(--muted)/.15)]" data-testid="whatsapp-messages">
        {messages.map((m) => <MessageBubble key={m.id} m={m} />)}
        <div ref={end} />
      </div>
      {draft && canAct && <DraftCard key={draft.id} draft={draft} windowOpen={conv.window_open} onDone={refresh} />}
      {canAct && <Composer convId={id} windowOpen={conv.window_open} onSent={refresh} />}
    </Card>
  );
}
